import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';

import { EditableText } from '../../../components/editable-text/EditableText';
import { updateCharacterCover } from '../character.slice';
import { InlineDropDown } from '../../../components/dropdowns/InlineDropDown';
import { formatPicture } from '../../../core/services/image.service';
import { ProfileAvatar } from '../../../components/profile-avatar/ProfileAvatar';
import { CandidateCard } from './candidate-card';
import { InlineTextArea } from '../../../components/editable-text/inline-text-area';

import { Candidate, Character } from '../../../app/entities';

import styles from './character-info-tab.module.scss';
export type CharacterInfoTabProps = {
  characterId: string;
};

type FormValue = Pick<
  Character,
  'name' | 'description' | 'personality' | 'characteristics' | 'appearance' | 'gender'
>;

const options = [
  { value: 'man', label: 'Man' },
  { value: 'woman', label: 'Woman' },
  { value: 'other', label: 'Other' },
];

export const CharacterInfoTab: React.FC<CharacterInfoTabProps> = observer(({ characterId }) => {
  const character = Character.getOne(characterId)!;

  const schema = yup.object().shape({
    name: yup
      .string()
      .matches(/^(.*)?\S+(.*)?$/, 'Name is required')
      .required('Name is required')
      .max(16, 'Max 16 characters'),
    description: yup.string().max(1024, 'Max 1024 characters'),
    personality: yup.string().max(1024, 'Max 1024 characters'),
    characteristics: yup.string().max(1024, 'Max 1024 characters'),
    appearance: yup.string().max(1024, 'Max 1024 characters'),
    gender: yup.string().oneOf(['man', 'woman', 'other'], 'Invalid gender'),
  });

  const {
    register,
    formState: { errors },
  } = useForm<FormValue>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: character.name,
      description: character.description,
      personality: character.personality,
      characteristics: character.characteristics,
      appearance: character.appearance,
      gender: character.gender,
    },
  });

  const handleEdit = async (data: Partial<Character>) => {
    await character.update(data);
  };

  const handleUploadPhoto = async (file?: File) => {
    if (!file) {
      return;
    }

    const options = { width: 340, height: 340 };
    const frame = await formatPicture(file, options);
    await updateCharacterCover({ characterId: character._id, file: frame });
  };

  const selectedCandidate =
    !!character.cast?.candidateId && Candidate.getOne(character.cast.candidateId);

  return (
    <div className={styles['container']}>
      <div className={styles['side-info']}>
        <div className={styles['uploader-container']}>
          <ProfileAvatar
            placeholder="text"
            defaultUrl={character?.cover?.src}
            onChange={handleUploadPhoto}
          />
        </div>
        <EditableText onSubmit={(name) => handleEdit({ name })}>
          <h1 className={styles['character-name']}>{character.name}</h1>
        </EditableText>
        <p className={styles['info-label']}>Character</p>
        <hr className={styles['line']} />

        {selectedCandidate && <CandidateCard candidate={selectedCandidate} isSelectedCandidate />}
      </div>
      <div className={styles['main-info']}>
        <h1 className={styles['character-info-title']}>Character Information</h1>

        <div className={styles['character-info-container']}>
          <div className={styles['row']}>
            <div className={styles['field-container']}>
              <p className={styles['info-label']}>Gender</p>
              <InlineDropDown
                value={character.gender || 'man'}
                label={character.gender || 'Man'}
                onChange={(gender) => handleEdit({ gender })}
                options={options}
              />
            </div>
          </div>

          <div className={styles['row']}>
            <div className={styles['field-container']}>
              <p className={styles['info-label']}>Description</p>
              <InlineTextArea
                {...register('description')}
                placeholder="Description"
                onSubmit={(e) => {
                  handleEdit({ description: e.target.value });
                }}
                errorMessage={errors.description?.message}
              />
            </div>

            <div className={styles['field-container']}>
              <p className={styles['info-label']}>Physical Characteristics</p>
              <InlineTextArea
                {...register('characteristics')}
                placeholder="Characteristics"
                onSubmit={(e) => {
                  handleEdit({ characteristics: e.target.value });
                }}
                errorMessage={errors.characteristics?.message}
              />
            </div>
          </div>

          <div className={styles['row']}>
            <div className={styles['field-container']}>
              <p className={styles['info-label']}>Personality</p>
              <InlineTextArea
                {...register('personality')}
                placeholder="Personality"
                onSubmit={(e) => {
                  handleEdit({ personality: e.target.value });
                }}
                errorMessage={errors.personality?.message}
              />
            </div>

            <div className={styles['field-container']}>
              <p className={styles['info-label']}>Appearance</p>
              <InlineTextArea
                {...register('appearance')}
                placeholder="Appearance"
                onSubmit={(e) => {
                  handleEdit({ appearance: e.target.value });
                }}
                errorMessage={errors.appearance?.message}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
